export type SupportedLanguage = 'de' | 'en' | 'fr' | 'it';

const DEFAULT_LANGUAGE: SupportedLanguage = 'en';
const I18N_CONFIG_KEY = import.meta.env.VITE_APP_I18N_CONFIG_KEY || 'i18nConfig';
const SUPPORTED_LANGUAGES = new Set<SupportedLanguage>(['de', 'en', 'fr', 'it']);

const isSupportedLanguage = (value: unknown): value is SupportedLanguage =>
  typeof value === 'string' && SUPPORTED_LANGUAGES.has(value as SupportedLanguage);

export const getStoredLanguage = (): SupportedLanguage => {
  if (typeof window === 'undefined') return DEFAULT_LANGUAGE;

  let storedConfig: string | null = null;
  try {
    storedConfig = window.localStorage.getItem(I18N_CONFIG_KEY);
  } catch {
    return DEFAULT_LANGUAGE;
  }
  if (!storedConfig) return DEFAULT_LANGUAGE;

  try {
    const parsedConfig = JSON.parse(storedConfig) as { selectedLang?: unknown };
    if (isSupportedLanguage(parsedConfig?.selectedLang)) return parsedConfig.selectedLang;
  } catch {
    // Invalid or legacy values are discarded below and replaced with the default language.
  }

  try {
    window.localStorage.removeItem(I18N_CONFIG_KEY);
  } catch {
    // Storage may be unavailable in strict private-browsing modes.
  }
  return DEFAULT_LANGUAGE;
};

export const setStoredLanguage = (language: string) => {
  const selectedLang = isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
  try {
    window.localStorage.setItem(I18N_CONFIG_KEY, JSON.stringify({ selectedLang }));
  } catch {
    // The active language remains available in memory when storage is unavailable.
  }
};
